import React, { Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { ErrorBoundary } from 'react-error-boundary'
import { ResidentAppActionTile } from '@pandiafi/components'

import { getLatestMessage, isNewMessage } from '../../../utils/newMessage'

import { useAPIGetResidentMessageTimeline } from 'api/apiHooks/useAPIGetResidentMessageTimeline'

interface MessagesActionTileProps {
  href: string
}

const MessagesActionTileWithCount: React.FC<MessagesActionTileProps> = ({
  href,
}) => {
  const { t } = useTranslation(['common'])
  const { data } = useAPIGetResidentMessageTimeline()
  const newMessageThreadsCount = data.messages.filter((thread) => {
    const latestMessage = getLatestMessage(thread.entries)
    return latestMessage !== undefined && isNewMessage(latestMessage)
  }).length

  return (
    <ResidentAppActionTile
      icon="email"
      href={href}
      count={newMessageThreadsCount}
      countLabel={t('common:taskLinks.newMessagesCount', {
        count: newMessageThreadsCount,
      })}
    >
      {t('common:taskLinks.messages')}
    </ResidentAppActionTile>
  )
}

/**
 * The messages action tile with the number of new message threads. While the
 * messages load, or if loading fails, the tile shows without the number.
 */
export const MessagesActionTile: React.FC<MessagesActionTileProps> = ({
  href,
}) => {
  const { t } = useTranslation(['common'])
  const tileWithoutCount = (
    <ResidentAppActionTile icon="email" href={href}>
      {t('common:taskLinks.messages')}
    </ResidentAppActionTile>
  )

  return (
    <ErrorBoundary fallback={tileWithoutCount}>
      <Suspense fallback={tileWithoutCount}>
        <MessagesActionTileWithCount href={href} />
      </Suspense>
    </ErrorBoundary>
  )
}
